/** Module-only warming: never mounts a page, fetches records, or performs actions. */
const loaders: Record<string, () => Promise<unknown>> = {
  '/my-listings': () => import('@/pages/Dashboard'),
  '/my-menu': () => import('@/pages/MyMenu'),
  '/desk': () => import('@/pages/YourDesk'),
  '/supplier-menus': () => import('@/pages/SupplierMenus'),
  '/buy-requests': () => import('@/pages/BuyRequests'),
  '/messages': () => import('@/pages/Messages'),
  '/admin': () => Promise.all([import('@/components/admin/AdminLayout'), import('@/pages/admin/AdminDashboard')]),
};

export function createRoutePrefetcher(importers: Record<string, () => Promise<unknown>>) {
  const pending = new Map<string, Promise<unknown>>();
  return (destination: string): Promise<unknown> => {
    const path = destination.split(/[?#]/)[0];
    const load = importers[path];
    if (!load) return Promise.resolve();
    const existing = pending.get(path);
    if (existing) return existing;
    const promise = Promise.resolve().then(load).catch(() => {
      // Speculation is silent; a real navigation still uses LazyLoadErrorBoundary.
      pending.delete(path);
    });
    pending.set(path, promise);
    return promise;
  };
}

export const prefetchRoute = createRoutePrefetcher(loaders);

type Connection = { saveData?: boolean; effectiveType?: string };
export function canWarmRoutes(connection?: Connection) {
  return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType ?? '');
}

/** Stagger visible phone tabs in idle time; cleanup survives page/header remounts. */
export function scheduleRouteWarmup(paths: string[], warm = prefetchRoute): () => void {
  const connection = (navigator as Navigator & { connection?: Connection }).connection;
  if (!canWarmRoutes(connection)) return () => {};
  const queue = [...new Set(paths)];
  let stopped = false;
  let cancelScheduled = () => {};
  const schedule = () => {
    if (stopped || !queue.length) return;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      cancelScheduled = () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(run, 1200);
      cancelScheduled = () => window.clearTimeout(id);
    }
  };
  const run = () => {
    if (stopped) return;
    if (document.visibilityState === 'hidden') return;
    const path = queue.shift();
    if (path) void warm(path).finally(schedule);
  };
  const resume = () => {
    cancelScheduled();
    if (document.visibilityState === 'visible') schedule();
  };
  document.addEventListener('visibilitychange', resume);
  schedule();
  return () => {
    stopped = true;
    cancelScheduled();
    document.removeEventListener('visibilitychange', resume);
  };
}